import { httpClient } from '../api/http-client';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/api';

export interface ItemPedido {
  productoId?: string;
  presentacionId?: string;
  nombreProducto: string;
  nombrePresentacion?: string;
  precioUnitario: number;
  cantidad: number;
  factor?: number;
}

export interface CreatePedidoRequest {
  clienteNombre: string;
  clienteEmail: string;
  clienteTelefono: string;
  tipoDocumento?: string;
  numeroDocumento?: string;
  direccion: string;
  ciudad: string;
  barrio: string;
  indicaciones?: string;
  descuento?: number;
  items: ItemPedido[];
  usuarioId?: string;
  metodoPago?: string;
  pagos?: Array<{ metodo: string; monto: number }>;
  aplicarIva?: boolean;
  aplicarEnvio?: boolean;
}

export interface PedidoResponse {
  id: string;
  numeroPedido: string;
  cliente: {
    nombre: string;
    email: string;
    telefono: string;
    tipoDocumento?: string;
    numeroDocumento?: string;
  };
  envio: {
    direccion: string;
    ciudad: string;
    barrio: string;
    indicaciones?: string;
  };
  totales: {
    subtotal: number;
    envio: number;
    descuento: number;
    total: number;
  };
  estado: string;
  motivoCancelacion?: string;
  metodoPago?: string;
  pagos?: Array<{ metodo: string; monto: number }>;
  vendedor: { id: string; nombre: string; email?: string } | null;
  wompiTransaccionId?: string;
  items: Array<{
    id: string;
    productoId?: string;
    nombreProducto: string;
    nombrePresentacion?: string;
    precioUnitario: number;
    precioCompra?: number;
    cantidad: number;
    subtotal: number;
    codigoBarras?: string;
    loteId?: string;
    numeroLote?: string;
    proveedor?: string;
  }>;
  fechas: {
    creadoEn: string;
    actualizadoEn: string;
    pagadoEn?: string;
    enviadoEn?: string;
    entregadoEn?: string;
  };
}

export interface PaginatedPedidos {
  data: PedidoResponse[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateOrderResponse {
  success: boolean;
  data: {
    id: string;
    numeroPedido: string;
    estado: string;
    total: number;
  };
  message?: string;
}

export const OrdersService = {
  async create(data: CreatePedidoRequest): Promise<CreateOrderResponse> {
    const response = await httpClient.post<CreateOrderResponse>('/orders', data);
    return response.data;
  },

  async getById(id: string): Promise<{ success: boolean; data: PedidoResponse; message?: string }> {
    const response = await httpClient.get<{ success: boolean; data: PedidoResponse; message?: string }>(`/orders/${id}`);
    return response.data;
  },

  async getByNumber(numero: string) {
    // Usar axios directamente para evitar el interceptor de auth que redirige al login
    const response = await axios.get(`${API_BASE_URL}/orders/by-number/${numero}`);
    // Desenvolver la respuesta del backend (formato envelope: { data: {...}, statusCode, timestamp })
    const envelope = response.data;
    if (envelope && typeof envelope === 'object' && 'data' in envelope) {
      return envelope.data; // Retorna { success: true, data: {...} }
    }
    return envelope;
  },

  async getByEmail(email: string, page = 1, limit = 10) {
    const response = await httpClient.get(`/orders/by-email/${email}`, {
      params: { page, limit },
    });
    return response.data;
  },

  async list(page = 1, limit = 10, estado?: string, search?: string, desde?: string, hasta?: string) {
    const response = await httpClient.get('/orders', {
      params: { page, limit, estado, search, desde, hasta },
    });
    return response.data;
  },

  async updateStatus(id: string, estado: string, motivo?: string) {
    const response = await httpClient.patch(`/orders/${id}/status`, { estado, motivo });
    return response.data;
  },

  async getMySales(params?: { desde?: string; hasta?: string; estado?: string; page?: number; limit?: number; vendedorId?: string }): Promise<{
    success: boolean;
    data?: PedidoResponse[];
    meta?: { total: number; page: number; limit: number; totalPages: number };
    message?: string;
  }> {
    const response = await httpClient.get<{
      success: boolean;
      data?: PedidoResponse[];
      meta?: { total: number; page: number; limit: number; totalPages: number };
      message?: string;
    }>('/orders/my-sales', { params });
    return response.data;
  },

  async getSummary(estado?: string, search?: string, desde?: string, hasta?: string): Promise<{
    success: boolean;
    data: {
      total: number;
      pendientes: number;
      procesando: number;
      entregados: number;
      totalVentas: number;
    };
  }> {
    const response = await httpClient.get<{
      success: boolean;
      data: {
        total: number;
        pendientes: number;
        procesando: number;
        entregados: number;
        totalVentas: number;
      };
    }>('/orders/summary', {
      params: { estado, search, desde, hasta },
    });
    return response.data;
  },
};
